import React, { useState } from 'react';
import { Box } from '@mui/material';
import { AttendanceRegistrationTypeDto, AttendanceStateDto } from '../../../../../api-service/clients';
import { SelectableButton } from './selectable-button';
import { AttendanceNotRelevantButton } from './attendance-not-relevant-button';
import { AttendanceSpecialConditionsWarning } from './attendance-special-conditions-warning';
import { AttendanceInternalNoteButton } from './attendance-internal-note-button';
import { attendanceColumnMarginLeft, attendanceColumnMinWidth, citizenRowMinHeight, notRelevantColumnMarginLeft, notRelevantColumnMinWidth } from './attendance-column-layout';
import { ColorPalette } from '../../../../../components/config';
import { authorityAreaPathName, citizenAreaPathName, citizenSocialDetailsPathName } from '../../../../citizen/authority/citizen-authority-area-pathnames';
import { AttendanceProtocolComment } from './attendance-protocol-comments';
import { AttendanceCommentDialog } from './attendance-comment-dialog';
import { ConfirmDialog } from '../../../../../components/confirm-dialog';
import { formatCheckInTime } from './attendance-time';
import { CitizenLessonAttendanceState, RegistrationDetailsEdit } from '../interfaces';
import { selectableAttendanceStates } from '../utils';
import { formatDate } from '../../../../../utilities';

const commentPreviewLimit = 100;

interface ICitizenAttendanceProtocolProps {
  citizenLessonAttendance: CitizenLessonAttendanceState;
  lessonBeginAt: Date | string;
  lessonEndAt: Date | string;
  showStartAttendanceRegistration: boolean;
  showEndAttendanceRegistration: boolean;
  onAttendanceStartChange: (citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) => void;
  onAttendanceEndChange: (citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) => void;
  onNotRelevantChange: (citizenId: string) => void;
  onSaveRegistrationDetails: (citizenId: string, start?: RegistrationDetailsEdit, end?: RegistrationDetailsEdit) => void;
  isSaving?: boolean;
  isLocked?: boolean;
  isReadOnly?: boolean;
}

type PendingStatusChange = { registrationType: AttendanceRegistrationTypeDto; attendanceType?: AttendanceStateDto } | { registrationType: 'notRelevant' };

export function CitizenAttendanceProtocol(props: ICitizenAttendanceProtocolProps) {
  const {
    citizenLessonAttendance,
    lessonBeginAt,
    lessonEndAt,
    onAttendanceStartChange,
    onAttendanceEndChange,
    onNotRelevantChange,
    onSaveRegistrationDetails,
    showStartAttendanceRegistration,
    showEndAttendanceRegistration,
    isSaving = false,
    isLocked = false,
    isReadOnly = false,
  } = props;

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [isInternalNoteShown, setIsInternalNoteShown] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange>();

  const { startRegistration, endRegistration, requiresStartCheck, requiresEndCheck } = citizenLessonAttendance;
  const startStatus = startRegistration?.status;
  const endStatus = endRegistration?.status;
  const isNotRelevant = startStatus === AttendanceStateDto.NotRelevant || endStatus === AttendanceStateDto.NotRelevant;
  const showStartCheck = showStartAttendanceRegistration && requiresStartCheck;
  const showEndCheck = showEndAttendanceRegistration && requiresEndCheck;
  const hasAttendanceChecks = showStartCheck || showEndCheck;
  const showAttendanceColumns = showStartAttendanceRegistration || showEndAttendanceRegistration;
  const hasRegistration = startStatus !== undefined || endStatus !== undefined;

  const meetingTimeProse = [
    startStatus === AttendanceStateDto.NotOnTime && startRegistration?.checkInTime ? `Borgeren kom klokken ${formatCheckInTime(startRegistration.checkInTime)}` : undefined,
    endStatus === AttendanceStateDto.NotOnTime && endRegistration?.checkInTime ? `Borger gik klokken ${formatCheckInTime(endRegistration.checkInTime)}` : undefined,
  ].filter(Boolean);

  const birthDateText = citizenLessonAttendance.birthDate ? `(${formatDate(citizenLessonAttendance.birthDate)})` : undefined;

  const comments = [startRegistration?.commentText, endRegistration?.commentText].filter(Boolean);
  const hasComments = comments.length > 0;
  const combinedComment = comments.join(' / ');
  const isCommentTruncated = combinedComment.length > commentPreviewLimit;
  const commentPreview = isCommentTruncated ? `${combinedComment.slice(0, commentPreviewLimit)}…` : combinedComment;

  const isPendingNotRelevant = pendingStatusChange?.registrationType === 'notRelevant';
  const isPendingAttendanceChange = pendingStatusChange !== undefined && pendingStatusChange.registrationType !== 'notRelevant' && pendingStatusChange.attendanceType !== undefined;
  const isPendingAttendanceRemoval =
    pendingStatusChange !== undefined && pendingStatusChange.registrationType !== 'notRelevant' && pendingStatusChange.attendanceType === undefined;
  const isPendingRemoval = isPendingAttendanceRemoval || (isPendingNotRelevant && isNotRelevant);

  function handleStartClick(attendanceType?: AttendanceStateDto) {
    const losesComment = isNotRelevant ? hasComments : Boolean(startRegistration?.commentText);
    if (losesComment) {
      setPendingStatusChange({ registrationType: AttendanceRegistrationTypeDto.Start, attendanceType });
      return;
    }
    onAttendanceStartChange(citizenLessonAttendance.citizenId, attendanceType);
  }

  function handleEndClick(attendanceType?: AttendanceStateDto) {
    const losesComment = isNotRelevant ? hasComments : Boolean(endRegistration?.commentText);
    if (losesComment) {
      setPendingStatusChange({ registrationType: AttendanceRegistrationTypeDto.End, attendanceType });
      return;
    }
    onAttendanceEndChange(citizenLessonAttendance.citizenId, attendanceType);
  }

  function handleNotRelevantClick() {
    if (hasComments) {
      setPendingStatusChange({ registrationType: 'notRelevant' });
      return;
    }
    onNotRelevantChange(citizenLessonAttendance.citizenId);
  }

  function handleResolvePendingStatusChange(keepComment: boolean) {
    if (!pendingStatusChange) {
      return;
    }
    const { citizenId } = citizenLessonAttendance;
    if (pendingStatusChange.registrationType === 'notRelevant') {
      onNotRelevantChange(citizenId);
    } else if (pendingStatusChange.registrationType === AttendanceRegistrationTypeDto.Start) {
      onAttendanceStartChange(citizenId, pendingStatusChange.attendanceType, keepComment);
    } else {
      onAttendanceEndChange(citizenId, pendingStatusChange.attendanceType, keepComment);
    }
    setPendingStatusChange(undefined);
  }

  function handleOpenCitizen() {
    window.open(`/${citizenAreaPathName}/${authorityAreaPathName}/${citizenLessonAttendance.citizenId}/${citizenSocialDetailsPathName}`);
  }

  const backgroundColor = getBackgroundColor(isLocked);

  return (
    <>
      <Box display="flex" alignItems="stretch" minHeight={citizenRowMinHeight}>
        <Box flex={1} minWidth={0} display="flex" sx={{ backgroundColor: 'white', borderRadius: 1, padding: 2 }}>
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" justifyContent="center">
            <Box display="flex" gap={1} alignItems="center">
              <Box sx={{ cursor: 'pointer', color: ColorPalette.PrimaryMain }} onClick={handleOpenCitizen}>
                {citizenLessonAttendance.citizenName}
              </Box>
              {birthDateText && <Box sx={{ fontSize: '0.8em', color: 'text.secondary' }}>{birthDateText}</Box>}
              <AttendanceSpecialConditionsWarning citizenLessonAttendance={citizenLessonAttendance} />
            </Box>
            {citizenLessonAttendance.internalNote && (
              <Box sx={{ fontSize: '0.8em', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Intern note:
                </Box>
                <AttendanceInternalNoteButton
                  text={citizenLessonAttendance.internalNote.text}
                  isInternalNoteShown={isInternalNoteShown}
                  onToggleInternalNote={() => setIsInternalNoteShown(!isInternalNoteShown)}
                />
                {isInternalNoteShown && citizenLessonAttendance.internalNote.text}
              </Box>
            )}
            {citizenLessonAttendance.primaryCaseworkerName && (
              <Box sx={{ fontSize: '0.8em', color: 'text.secondary' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Sagsbehandler:
                </Box>
                {` ${citizenLessonAttendance.primaryCaseworkerName}`}
              </Box>
            )}
            {isNotRelevant && <Box sx={{ fontSize: '0.8em', color: 'text.secondary' }}>Det er ikke relevant at registrere borgers fremmøde for denne lektion</Box>}
            {meetingTimeProse.map(prose => (
              <Box key={prose} sx={{ fontSize: '0.8em', color: 'text.secondary' }}>
                {prose}
              </Box>
            ))}
            {combinedComment && (
              <Box sx={{ fontSize: '0.8em', color: 'text.secondary' }}>
                {commentPreview}
                {isCommentTruncated && (
                  <Box component="span" sx={{ cursor: 'pointer', color: ColorPalette.PrimaryMain, marginLeft: 0.5 }} onClick={() => setIsCommentDialogOpen(true)}>
                    Se resten
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {hasAttendanceChecks && (
            <Box alignSelf="flex-start">
              <AttendanceProtocolComment
                hasRegistration={hasRegistration && !isLocked && !isReadOnly}
                endComment={endRegistration?.commentText}
                onClick={() => setIsCommentDialogOpen(true)}
                startComment={startRegistration?.commentText}
              />
            </Box>
          )}
        </Box>
        {showStartAttendanceRegistration &&
          (showStartCheck ? (
            <AttendanceButtons attendanceState={startRegistration?.status} onClick={handleStartClick} disabled={isSaving} isLocked={isLocked} readOnly={isReadOnly} />
          ) : (
            <AttendanceColumnSpacer minWidth={attendanceColumnMinWidth} marginLeft={attendanceColumnMarginLeft} />
          ))}
        {showEndAttendanceRegistration &&
          (showEndCheck ? (
            <AttendanceButtons isEnd attendanceState={endRegistration?.status} onClick={handleEndClick} disabled={isSaving} isLocked={isLocked} readOnly={isReadOnly} />
          ) : (
            <AttendanceColumnSpacer minWidth={attendanceColumnMinWidth} marginLeft={attendanceColumnMarginLeft} />
          ))}
        {showAttendanceColumns &&
          (hasAttendanceChecks ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginLeft={notRelevantColumnMarginLeft}
              minWidth={notRelevantColumnMinWidth}
              sx={{ backgroundColor: backgroundColor, borderRadius: 1 }}
            >
              <AttendanceNotRelevantButton selected={isNotRelevant} onClick={handleNotRelevantClick} disabled={isSaving} locked={isLocked} readOnly={isReadOnly} />
            </Box>
          ) : (
            <AttendanceColumnSpacer minWidth={notRelevantColumnMinWidth} marginLeft={notRelevantColumnMarginLeft} />
          ))}
      </Box>
      {hasAttendanceChecks && (
        <AttendanceCommentDialog
          open={isCommentDialogOpen}
          citizenLessonAttendance={citizenLessonAttendance}
          lessonBeginAt={lessonBeginAt}
          lessonEndAt={lessonEndAt}
          showStartAttendanceRegistration={showStartCheck}
          showEndAttendanceRegistration={showEndCheck}
          readOnly={isLocked || isReadOnly}
          onClose={() => setIsCommentDialogOpen(false)}
          onSave={onSaveRegistrationDetails}
        />
      )}
      <ConfirmDialog
        type="warning"
        open={isPendingAttendanceChange && !isNotRelevant}
        title="Fremmødet har en kommentar"
        message="Ændring af fremmødestatus fjerner den kommentar, der er oprettet på fremmødet. Vil du beholde eller slette kommentaren?"
        confirmButtonText="Behold kommentar"
        onConfirmClick={() => handleResolvePendingStatusChange(true)}
        cancelButtonText="Slet kommentar"
        onCancelClick={() => handleResolvePendingStatusChange(false)}
        onClose={() => setPendingStatusChange(undefined)}
      />
      <ConfirmDialog
        type="warning"
        open={isPendingNotRelevant && !isNotRelevant}
        title="Kommentarer bliver slettet"
        message="Registreres fremmødet som ikke relevant, slettes kommentarer, der er oprettet på borgerens fremmøde."
        confirmButtonText="Ja, ændr fremmøde"
        onConfirmClick={() => handleResolvePendingStatusChange(false)}
        cancelButtonText="Afbryd"
        onCancelClick={() => setPendingStatusChange(undefined)}
      />
      <ConfirmDialog
        type="warning"
        open={isPendingAttendanceChange && isNotRelevant}
        title="Kommentaren bliver slettet"
        message="Ændres fremmødet fra ikke relevant, slettes den kommentar, der er oprettet på borgerens fremmøde."
        confirmButtonText="Ja, ændr fremmøde"
        onConfirmClick={() => handleResolvePendingStatusChange(false)}
        cancelButtonText="Afbryd"
        onCancelClick={() => setPendingStatusChange(undefined)}
      />
      <ConfirmDialog
        type="warning"
        open={isPendingRemoval}
        title="Kommentaren bliver slettet"
        message="Fjernes registreringen, slettes den kommentar, der er oprettet på borgerens fremmøde."
        confirmButtonText="Ja"
        onConfirmClick={() => handleResolvePendingStatusChange(false)}
        cancelButtonText="Afbryd"
        onCancelClick={() => setPendingStatusChange(undefined)}
      />
    </>
  );
}

// Reserves the column's width without drawing it, so the remaining columns stay aligned with the header.
function AttendanceColumnSpacer({ minWidth, marginLeft }: { minWidth: number; marginLeft: string }) {
  return <Box marginLeft={marginLeft} minWidth={minWidth} />;
}

interface IAttendanceButtonsProps {
  attendanceState?: AttendanceStateDto;
  isEnd?: boolean;
  onClick: (attendanceType?: AttendanceStateDto) => void;
  disabled?: boolean;
  isLocked?: boolean;
  readOnly?: boolean;
}

export function AttendanceButtons(props: IAttendanceButtonsProps) {
  const { onClick, attendanceState, isEnd = false, disabled = false, isLocked = false, readOnly = false } = props;
  const registrationType = isEnd ? AttendanceRegistrationTypeDto.End : AttendanceRegistrationTypeDto.Start;

  const backgroundColor = getBackgroundColor(isLocked);

  return (
    <Box display="flex" alignItems="center" marginLeft={attendanceColumnMarginLeft} sx={{ backgroundColor: backgroundColor, borderRadius: 1 }}>
      {selectableAttendanceStates.map(selectableAttendanceState => (
        <SelectableButton
          key={selectableAttendanceState}
          attendanceState={selectableAttendanceState}
          registrationType={registrationType}
          onClick={onClick}
          selectedAttendanceState={attendanceState}
          disabled={disabled}
          locked={isLocked}
          readOnly={readOnly}
        />
      ))}
    </Box>
  );
}

function getBackgroundColor(isLocked: boolean) {
  return isLocked ? ColorPalette.DisabledInputBackground : 'white';
}
